import { useState } from 'react';
import { ArrowLeft, Camera, User, Phone, Mail, MapPin, Save, X } from 'lucide-react';

interface EditProfileScreenProps {
  onBack: () => void;
  onSave: (data: any) => void;
}

export function EditProfileScreen({ onBack, onSave }: EditProfileScreenProps) {
  const [formData, setFormData] = useState({
    firstName: 'Adebayo',
    lastName: 'Ogunlesi',
    phone: '+234 810 123 4567',
    email: 'adebayo.o@email.com',
    dateOfBirth: '1995-06-15',
    address: '45 Herbert Macaulay Way, Yaba, Lagos',
    city: 'Lagos',
    state: 'Lagos',
    emergencyContactName: 'Chioma Ogunlesi',
    emergencyContactPhone: '+234 809 876 5432',
  });

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleImageUpload = () => {
    // Simulate image upload
    alert('Image upload would open camera/gallery here');
    setProfileImage('https://i.pravatar.cc/150?img=12');
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(formData);
    alert('Profile updated successfully! ✓');
    onBack();
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to discard them?')) {
        onBack();
      }
    } else {
      onBack();
    }
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={handleCancel} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
            <ArrowLeft className="w-6 h-6 text-[#111111]" />
          </button>
          <h1 className="text-[18px] text-[#111111]">Edit Profile</h1>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009963] transition-colors flex items-center gap-2 text-[14px]"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Profile Photo */}
        <div className="mb-8">
          <label className="block text-[14px] text-[#6B6B6B] mb-3">Profile Photo</label>
          <div className="flex items-center gap-4">
            <div className="relative">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <span className="text-[32px] text-[#E0001B]">
                    {formData.firstName[0]}{formData.lastName[0]}
                  </span>
                </div>
              )}
              <button
                onClick={handleImageUpload}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#E0001B] text-white flex items-center justify-center shadow-lg hover:bg-[#C00017] transition-colors"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <button
                onClick={handleImageUpload}
                className="px-4 py-2 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors text-[14px] mb-2"
              >
                Change Photo
              </button>
              {profileImage && (
                <button
                  onClick={() => {
                    setProfileImage(null);
                    setHasChanges(true);
                  }}
                  className="block px-4 py-2 rounded-[12px] text-red-600 hover:bg-red-50 transition-colors text-[14px]"
                >
                  Remove Photo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="mb-8">
          <h2 className="text-[18px] font-medium mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#E0001B]" />
            Personal Information
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[14px] text-[#6B6B6B] mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[14px] text-[#6B6B6B] mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Date of Birth</label>
              <input
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-8">
          <h2 className="text-[18px] font-medium mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#E0001B]" />
            Contact Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6B6B]" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
              </div>
              <p className="text-[12px] text-[#6B6B6B] mt-1">
                ⚠️ Changing phone number requires verification
              </p>
            </div>

            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6B6B]" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="mb-8">
          <h2 className="text-[18px] font-medium mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#E0001B]" />
            Address
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Street Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[14px] text-[#6B6B6B] mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[14px] text-[#6B6B6B] mb-2">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                >
                  <option value="Lagos">Lagos</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Rivers">Rivers</option>
                  <option value="Oyo">Oyo</option>
                  <option value="Kano">Kano</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="mb-8">
          <h2 className="text-[18px] font-medium mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-red-600" />
            Emergency Contact
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Contact Name</label>
              <input
                type="text"
                value={formData.emergencyContactName}
                onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-[14px] text-[#6B6B6B] mb-2">Contact Phone</label>
              <input
                type="tel"
                value={formData.emergencyContactPhone}
                onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pb-8">
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="w-full h-14 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009963] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-[16px]"
          >
            <Save className="w-5 h-5" />
            Save Changes
          </button>

          <button
            onClick={handleCancel}
            className="w-full h-14 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 font-medium text-[16px]"
          >
            <X className="w-5 h-5" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
